import { axiosInstance } from '../../utils/axios'
import type {
  Device,
  DeviceListResponse,
  DeviceUpdateRequest,
  AssignPartnerRequest,
  AssignPartnerResponse,
  AssignGroupRequest,
  AssignGroupResponse,
  Group,
  GroupDetail,
  GroupCreateRequest,
  GroupUpdateRequest,
  User,
  UserDetail,
  UserUpdateRequest,
  UnitCategory,
  UnitCategoryDetail,
  UnitCategoryCreateRequest,
  UnitCategoryUpdateRequest,
  UnitFormulaCreateRequest,
  DeleteResponse,
  ApiResponse,
  PendingAccount,
  PendingAccountCreateRequest,
  PendingAccountCreateResponse,
  ResendInvitationResponse,
} from '../../types/admin'

const BASE_URL = '/admin'

/**
 * API Client wrapper for admin operations
 * Uses the existing axios instance with built-in auth token injection
 */
const apiClient = {
  get: async <T>(url: string): Promise<T> => {
    const response = await axiosInstance.get<T>(url)
    return response.data
  },

  post: async <T>(url: string, data?: unknown): Promise<T> => {
    const response = await axiosInstance.post<T>(url, data)
    return response.data
  },

  put: async <T>(url: string, data?: unknown): Promise<T> => {
    const response = await axiosInstance.put<T>(url, data)
    return response.data
  },

  delete: async <T>(url: string, config?: { data?: unknown }): Promise<T> => {
    const response = await axiosInstance.delete<T>(url, config)
    return response.data
  },
}

// ============================================================================
// Device API
// ============================================================================

export const deviceApi = {
  /**
   * List devices with pagination, search, and sorting
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 100)
   * @param search - Search query string (optional)
   * @param sortBy - Field to sort by (optional)
   * @param sortDir - Sort direction 'asc' or 'desc' (optional)
   */
  list: (page = 1, limit = 100, search = '', sortBy = '', sortDir: 'asc' | 'desc' = 'asc') => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (search.trim()) {
      params.append('search', search.trim())
    }

    if (sortBy) {
      params.append('sortBy', sortBy)
      params.append('sortDir', sortDir)
    }

    return apiClient.get<ApiResponse<DeviceListResponse>>(`${BASE_URL}/devices?${params.toString()}`)
  },

  /**
   * Get a single device by ID
   * @param id - Device ID
   */
  get: (id: number) => apiClient.get<ApiResponse<Device>>(`${BASE_URL}/devices/${id}`),

  /**
   * Update device configuration
   * @param id - Device ID
   * @param data - Device update payload
   */
  update: (id: number, data: DeviceUpdateRequest) =>
    apiClient.put<ApiResponse<{ deviceId: number; updated: boolean }>>(`${BASE_URL}/devices/${id}`, data),

  /**
   * Bulk-assign or clear partner ownership across multiple devices.
   * partnerUserId = null clears the assignment.
   */
  assignPartner: (data: AssignPartnerRequest) =>
    apiClient.post<ApiResponse<AssignPartnerResponse>>(`${BASE_URL}/devices/assign-partner`, data),

  /**
   * Bulk-assign devices to a group (location). locationId = 0 unassigns
   * the devices into the system Unassigned group.
   */
  assignGroup: (data: AssignGroupRequest) =>
    apiClient.post<ApiResponse<AssignGroupResponse>>(`${BASE_URL}/devices/assign-group`, data),
}

// ============================================================================
// Group API
// ============================================================================

export const groupApi = {
  /**
   * List all groups
   */
  list: () => apiClient.get<ApiResponse<Group[]>>(`${BASE_URL}/groups`),

  /**
   * Get group details including users and devices
   * @param id - Group (location) ID
   */
  get: (id: number) => apiClient.get<ApiResponse<GroupDetail>>(`${BASE_URL}/groups/${id}`),

  /**
   * Create a new group
   * @param data - Group creation payload
   */
  create: (data: GroupCreateRequest) => apiClient.post<ApiResponse<{ locationId: number }>>(`${BASE_URL}/groups`, data),

  /**
   * Update group details
   * @param id - Group (location) ID
   * @param data - Group update payload
   */
  update: (id: number, data: GroupUpdateRequest) =>
    apiClient.put<ApiResponse<{ locationId: number; updated: boolean }>>(`${BASE_URL}/groups/${id}`, data),

  /**
   * Delete multiple groups
   * @param locationIds - Array of location IDs to delete
   */
  delete: (locationIds: number[]) =>
    apiClient.delete<ApiResponse<DeleteResponse>>(`${BASE_URL}/groups`, { data: { locationIds } }),
}

// ============================================================================
// User API
// ============================================================================

export const userApi = {
  /**
   * List all users
   */
  list: () => apiClient.get<ApiResponse<User[]>>(`${BASE_URL}/users`),

  /**
   * Get user details including group memberships
   * @param id - User ID
   */
  get: (id: number) => apiClient.get<ApiResponse<UserDetail>>(`${BASE_URL}/users/${id}`),

  /**
   * Mint a one-time password-recovery link for a user.
   * The link is returned to the admin to deliver themselves; it is never
   * emailed and never stored, so treat it as a credential.
   */
  resetLink: (id: number) =>
    apiClient.post<ApiResponse<{ link: string; email: string }>>(`${BASE_URL}/users/${id}/reset-link`),

  /**
   * Set a user's password directly.
   * Omit `password` to have the server generate a temporary one and return it;
   * a password we sent is never echoed back. Either way the value is a
   * credential — do not cache it or write it anywhere.
   */
  setPassword: (id: number, password?: string) =>
    apiClient.post<ApiResponse<{ email: string; generated: boolean; password?: string }>>(
      `${BASE_URL}/users/${id}/password`,
      password ? { password } : {}
    ),

  /**
   * Update user profile and permissions
   * @param id - User ID
   * @param data - User update payload
   */
  update: (id: number, data: UserUpdateRequest) =>
    apiClient.put<ApiResponse<{ userId: number; updated: boolean }>>(`${BASE_URL}/users/${id}`, data),

  /**
   * Replace the set of groups a user can access, without touching their
   * profile. The array is the complete desired set — anything absent is
   * revoked.
   */
  updateGroups: (id: number, groupIds: number[]) =>
    apiClient.put<ApiResponse<{ userId: number; groupIds: number[] }>>(
      `${BASE_URL}/users/${id}/groups`,
      { groupIds }
    ),

  /**
   * Delete multiple users
   * @param userIds - Array of user IDs to delete
   */
  delete: (userIds: number[]) =>
    apiClient.delete<ApiResponse<DeleteResponse>>(`${BASE_URL}/users`, { data: { userIds } }),
}

// ============================================================================
// Unit Category API
// ============================================================================

export const unitCategoryApi = {
  /**
   * List all unit categories
   */
  list: () => apiClient.get<ApiResponse<UnitCategory[]>>(`${BASE_URL}/unit-categories`),

  /**
   * Get unit category details including formulas
   * @param id - Unit category ID
   */
  get: (id: number) => apiClient.get<ApiResponse<UnitCategoryDetail>>(`${BASE_URL}/unit-categories/${id}`),

  /**
   * Create a new unit category
   * @param data - Unit category creation payload
   */
  create: (data: UnitCategoryCreateRequest) =>
    apiClient.post<ApiResponse<{ ucatId: number }>>(`${BASE_URL}/unit-categories`, data),

  /**
   * Update unit category details
   * @param id - Unit category ID
   * @param data - Partial unit category update payload
   */
  update: (id: number, data: Partial<UnitCategoryUpdateRequest>) =>
    apiClient.put<ApiResponse<{ ucatId: number; updated: boolean }>>(`${BASE_URL}/unit-categories/${id}`, data),

  /**
   * Delete multiple unit categories
   * @param ucatIds - Array of unit category IDs to delete
   */
  delete: (ucatIds: number[]) =>
    apiClient.delete<ApiResponse<DeleteResponse>>(`${BASE_URL}/unit-categories`, { data: { ucatIds } }),

  // --------------------------------------------------------------------------
  // Unit Formula Operations
  // --------------------------------------------------------------------------

  /**
   * Create a new unit formula for a category
   * @param categoryId - Unit category ID
   * @param data - Unit formula creation payload
   */
  createFormula: (categoryId: number, data: UnitFormulaCreateRequest) =>
    apiClient.post<ApiResponse<{ untfrmId: number }>>(`${BASE_URL}/unit-categories/${categoryId}/formulas`, data),

  /**
   * Update unit formula details
   * @param id - Unit formula ID
   * @param data - Partial unit formula update payload
   */
  updateFormula: (id: number, data: Partial<UnitFormulaCreateRequest>) =>
    apiClient.put<ApiResponse<{ untfrmId: number; updated: boolean }>>(`${BASE_URL}/unit-formulas/${id}`, data),

  /**
   * Delete multiple unit formulas
   * @param untfrmIds - Array of unit formula IDs to delete
   */
  deleteFormulas: (untfrmIds: number[]) =>
    apiClient.delete<ApiResponse<DeleteResponse>>(`${BASE_URL}/unit-formulas`, { data: { untfrmIds } }),
}

// ============================================================================
// Pending Accounts API
// ============================================================================

export const pendingAccountApi = {
  /**
   * List all pending accounts (invited but not yet registered)
   */
  list: () => apiClient.get<ApiResponse<PendingAccount[]>>(`${BASE_URL}/pending-accounts`),

  /**
   * Create a new pending account invitation
   * @param data - Pending account creation payload
   */
  create: (data: PendingAccountCreateRequest) =>
    apiClient.post<ApiResponse<PendingAccountCreateResponse>>(`${BASE_URL}/pending-accounts`, data),

  /**
   * Delete multiple pending accounts
   * @param ids - Array of pending account IDs (UUIDs) to delete
   */
  delete: (ids: string[]) =>
    apiClient.delete<ApiResponse<DeleteResponse>>(`${BASE_URL}/pending-accounts`, { data: { ids } }),

  /**
   * Delete a single pending account
   * @param id - Pending account ID (UUID) to delete
   */
  deleteOne: (id: string) => apiClient.delete<ApiResponse<DeleteResponse>>(`${BASE_URL}/pending-accounts/${id}`),

  /**
   * Resend invitation to a pending account
   * @param id - Pending account ID (UUID) to resend invitation to
   */
  resendInvite: (id: string) =>
    apiClient.post<ApiResponse<ResendInvitationResponse>>(`${BASE_URL}/pending-accounts/${id}/resend`),
}
